// apps/web/src/features/crm/leads/card/LeadCardTitle.tsx
//
// Header title + manager edit-in-place (#2308, D-5). Read display is the
// resolved `title ?? name ?? unnamed`; a manager gets a pencil that swaps the
// title for an inline `Input`. Enter or blur saves via the LEGACY
// `usePatchLead({ title })` (server normalizes empty/whitespace → null); Esc
// cancels without saving. `usePatchLead` only invalidates `leadsKeys`, so on
// success we ALSO flush the Engagement projection (`engagementsKeys.all`, a
// prefix of the fidelity detail key) so the header re-reads the new title
// immediately without a reload.
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { engagementsKeys, usePatchLead } from '@magimp/shared-hooks';
import { IconButton, Input } from '../../../../ui/index.js';
import { useToastStore } from '../../../../stores/toast.js';
import { errorMessage } from '../../../../api/errors.js';

export interface LeadCardTitleProps {
  leadId: string;
  /** Resolved display title (`title ?? name ?? unnamed`). */
  title: string;
  /** Raw `Lead.title` used to seed the editor (may be null/undefined). */
  rawTitle: string | null | undefined;
  canManage: boolean;
}

export function LeadCardTitle({ leadId, title, rawTitle, canManage }: LeadCardTitleProps) {
  const { t } = useTranslation(['leads']);
  const qc = useQueryClient();
  const show = useToastStore((s) => s.show);
  const patch = usePatchLead(leadId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // WHY: Esc sets editing→false, which unmounts the input; the resulting blur
  // must NOT re-fire a save. This ref carries the intent across that unmount.
  const skipSave = useRef(false);

  const begin = () => {
    setDraft(rawTitle ?? '');
    skipSave.current = false;
    setEditing(true);
  };

  const cancel = () => {
    skipSave.current = true;
    setEditing(false);
  };

  const save = () => {
    if (skipSave.current) return;
    setEditing(false);
    // Empty/whitespace is allowed — the server + schema normalize it to null.
    patch.mutate(
      { title: draft },
      {
        onSuccess: () => {
          show({ tone: 'success', text: t('leads:card.titleEdit.saved') });
          void qc.invalidateQueries({ queryKey: engagementsKeys.all });
        },
        onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
      },
    );
  };

  if (editing) {
    return (
      <Input
        value={draft}
        onChange={setDraft}
        // Intentional mount-only focus: the editor appears only on the pencil
        // click, so focus must land on the input the manager just opened.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        ariaLabel={t('leads:card.titleEdit.placeholder')}
        placeholder={t('leads:card.titleEdit.placeholder')}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            save();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        style={{ maxWidth: 360, height: 'var(--control-h-sm)' }}
      />
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <span
        data-testid="lead-card-title"
        style={{
          fontSize: 17,
          fontWeight: 600,
          color: 'var(--fg-1)',
          letterSpacing: '-.01em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </span>
      {canManage && (
        <IconButton
          icon="pencil-line"
          label={t('leads:card.titleEdit.edit')}
          size={24}
          onClick={begin}
        />
      )}
    </span>
  );
}
